import { useAuth } from "@/context/AuthContext"
import { clsx, type ClassValue } from "clsx"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// Custom hook to get the formatDateToDisplay function
export function useFormatDateToDisplay() {
  const { user } = useAuth();
  const { i18n } = useTranslation();

  const preferredTimeAnnotation = user?.settings?.time_format || '24h';

  return useMemo(() => {
    return (dateString: string | number, format?: string) => {
      // Always parse as UTC, especially for Zulu timestamps (ending with 'Z')
      let date: Date;
      if (typeof dateString === 'number') {
        date = new Date(dateString * 1000);
      } else if (typeof dateString === 'string' && dateString.endsWith('Z')) {
        date = new Date(dateString);
      } else {
        date = new Date(dateString);
      }

      const use12Hour = preferredTimeAnnotation === '12h';

      if (format) {
        // format can be 'time', 'date', 'datetime' or 'short'
        if (format === 'time') {
          return date.toLocaleTimeString(i18n.language, {
            hour: '2-digit',
            minute: '2-digit',
            hour12: use12Hour,
            timeZone: 'UTC',
          });
        }

        if (format === 'short') {
          return date.toLocaleString(i18n.language, {
            day: 'numeric',
            month: 'short',
            hour: '2-digit',
            minute: '2-digit',
            hour12: use12Hour,
            timeZone: 'UTC',
          });
        }
        
        if (format === 'date') {
          return date.toLocaleDateString(i18n.language, {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
            timeZone: 'UTC',
          });
        }

        if (format === 'datetime') {
          return date.toLocaleString(i18n.language, {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: use12Hour,
            timeZone: 'UTC',
          });
        }
      }

      const options: Intl.DateTimeFormatOptions = {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: use12Hour,
        timeZone: 'UTC',
      };

      const formattedDate = date.toLocaleString(i18n.language, options);
      const timeZoneName = Intl.DateTimeFormat(i18n.language, { timeZone: 'UTC', timeZoneName: 'short' })
        .formatToParts(date)
        .find(part => part.type === 'timeZoneName')?.value || 'UTC';
      return `${formattedDate} (${timeZoneName})`;
    };
  }, [i18n.language, preferredTimeAnnotation]);
}